<template>
  <div>
    <b-modal 
      id="AccessControlModal"
      size="xl"
      content-class="menu-bg-blue-transparent-modal ac-modal"
      title="Access Control"
      hide-footer 
      
      @show="initTables()"
    >
      <b-tabs align="center" pills card class="ac-tabs">
        <!-- Access Roles -->
        <b-tab title="Access Roles" active>
          <vue-bootstrap4-table
            id="AccessControlTable"
            :rows="accessRoles" 
            :columns="accessRolesColumns" 
            :config="config" 
            :classes="classes"
            :actions="actions"
            @add="initAddRole()"
          >
              <template slot="module_names" slot-scope="props">
                  <span class="module-name-cell" :title="displaySortedModuleNames(props.row.module_names)">
                      {{ displaySortedModuleNames(props.row.module_names) }}
                  </span>
              </template>
              <template slot="isactive" slot-scope="props">
                  <span v-if="props.row.isactive" class="status-badge status-active">ACTIVE</span>
                  <span v-else class="status-badge status-inactive">INACTIVE</span>
              </template>
              <template slot="modified_date" slot-scope="props">
                  <span class="date-cell">{{ props.row.modified_date.split('T')[0] }}</span>
              </template>
              <template slot="update" slot-scope="props">
                  <button class="btn ac-icon-btn ac-icon-btn--edit" @click="initUpdateRole(props.row)" :disabled="!props.row.isactive">
                      <fa :icon="['fas', 'pencil-alt']" size="lg" />
                  </button>
              </template>
              <template slot="activate" slot-scope="props">
                  <button v-if="!props.row.isactive" class="btn ac-icon-btn ac-icon-btn--activate" @click="initStatusChangeModal(props.row)">
                      <fa :icon="['fas', 'lock-open']" size="lg" title="Activate Access Role" />
                  </button>
                  <button class="btn ac-icon-btn ac-icon-btn--deactivate" v-else @click="initStatusChangeModal(props.row)">
                      <fa :icon="['fas', 'lock']" size="lg" title="Deactivate Access Role" />
                  </button>
              </template>
          </vue-bootstrap4-table>
        </b-tab>

        <!-- Organizations -->
        <b-tab title="Organizations">
          <vue-bootstrap4-table
            id="organizationsTable"
            :rows="organizations" 
            :columns="orgColumns" 
            :config="config" 
            :classes="classes"
            :actions="orgActions"
            @add="initAddOrg()"
          >
              <template slot="updated_at" slot-scope="props">
                  <span v-if="props.row.updated_at" class="date-cell">{{ props.row.updated_at.split('T')[0] }}</span>
              </template>
              <template slot="update" slot-scope="props">
                  <button class="btn ac-icon-btn ac-icon-btn--edit" @click="initUpdateOrg(props.row)">
                      <fa :icon="['fas', 'pencil-alt']" size="lg" />
                  </button>
              </template>
              <template slot="isactive" slot-scope="props">
                <b-button 
                  variant="transparent" 
                  class="ac-icon-btn"
                  :class="props.row.isactive ? 'ac-icon-btn--deactivate' : 'ac-icon-btn--activate'"
                  @click="changeOrgStatusHandler(props.row)"
                  @mouseover="hoveredOrgId = props.row.id"
                  @mouseleave="hoveredOrgId = null"
                >
                  <fa
                    v-if="!props.row.isactive"
                    :icon="['fas', hoveredOrgId === props.row.id ? 'lock-open' : 'lock']"
                    size="lg"
                  />
                  <fa
                    v-else
                    :icon="['fas', hoveredOrgId === props.row.id ? 'lock' : 'lock-open']"
                    size="lg"
                  />
                </b-button>
              </template>
          </vue-bootstrap4-table>
        </b-tab>

        <!-- Layers -->
        <b-tab title="Layers">
          <div v-if="layers" v-for="(layerGroup, index) in layers" :key="layerGroup.project_id">
            <div class="ac-section-header" @click="collapseProject(layerGroup, index)">
              <div class="ac-section-title-row">
                <span class="ac-section-dot ac-dot--layers"></span>
                <span class="ac-section-title">{{ layerGroup.project }}</span>
              </div>
              <div class="ac-chevron" :class="{ open: layerGroup.collapsed }">
                <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>

            <b-collapse v-model="layerGroup.collapsed" class="ac-collapse-body">
              <!-- Nested part numbers -->
              <div v-if="layerGroup.part_numbers[0].part_number" v-for="(partNumber, index) in layerGroup.part_numbers" :key="partNumber.release_id">
                <div class="ac-section-header ac-section-header--nested" @click="collapsePartNumber(partNumber)">
                  <div class="ac-section-title-row">
                    <span class="ac-section-dot ac-dot--part"></span>
                    <span class="ac-section-title ac-section-title--sm">{{ partNumber.part_number }}</span>
                  </div>
                  <div class="ac-chevron" :class="{ open: partNumber.collapsed }">
                    <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
                  </div>
                </div>
                <b-collapse v-model="partNumber.collapsed" class="ac-collapse-body">
                  <div class="ac-layer-grid">
                    <div v-for="(layer, index) in partNumber.layers" :key="layer.layer_id" class="ac-layer-item">
                      <label class="ac-checkbox-label">
                        <input type="checkbox" class="ac-checkbox" :value="layer" v-model="layer.visibility" @change="layerChange(layer, partNumber)"/>
                        <span class="ac-checkbox-custom"></span>
                        <span class="ac-layer-name">{{ layer.display_name }}</span>
                        <span v-if="layer.saved && layer.spinner" class="ac-layer-status ac-layer-status--saved">
                          <fa :icon="['fas', 'check']" size="sm" />
                        </span>
                        <span v-else-if="layer.spinner" class="ac-layer-status">
                          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        </span>
                      </label>
                    </div>
                  </div>
                </b-collapse>
              </div>

              <!-- No part numbers, flat layers -->
              <div v-else class="ac-layer-grid">
                <div v-for="(layer, index) in layerGroup.part_numbers[0].layers" :key="layer.layer_id" class="ac-layer-item">
                  <label class="ac-checkbox-label">
                    <input type="checkbox" class="ac-checkbox" :value="layer" v-model="layer.visibility" />
                    <span class="ac-checkbox-custom"></span>
                    <span class="ac-layer-name">{{ layer.display_name }}</span>
                    <span v-if="layer.saved && layer.spinner" class="ac-layer-status ac-layer-status--saved">
                      <fa :icon="['fas', 'check']" size="sm" />
                    </span>
                    <span v-else-if="layer.spinner" class="ac-layer-status">
                      <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    </span>
                  </label>
                </div>
              </div>
            </b-collapse>
          </div>
        </b-tab>

        <!-- Modules -->
        <b-tab title="Modules">
          <!-- Tools -->
          <div class="ac-module-section">
            <div class="ac-section-header ac-section-header--tools" @click="toolsCollapsed = !toolsCollapsed">
              <div class="ac-section-title-row">
                <span class="ac-section-dot ac-dot--tools"></span>
                <span class="ac-section-title">MMP Tools</span>
                <span class="ac-badge ac-badge--tools">{{ allModules.filter(m => m.module_type === 'tool').length }}</span>
              </div>
              <div class="ac-chevron" :class="{ open: toolsCollapsed }">
                <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
            <b-collapse v-model="toolsCollapsed">
              <div class="ac-module-grid">
                <div
                  v-for="module in allModules.filter(m => m.module_type === 'tool')"
                  :key="module.module_name"
                  class="ac-module-chip ac-module-chip--tools"
                  :class="!module.isactive ? 'inactive-module': ''"
                  v-b-tooltip.hover.right="{ title: module.module_description, customClass: 'ac-tooltip' }"
                  @click="editModule(module)"
                >
                  <span class="ac-chip-label">{{ module.module_name }}</span>
                  <div class="inactive-tag" :style="{visibility: module.isactive ? 'hidden' : 'visible'}">INACTIVE</div>
                </div>

                <!-- Add Button -->
                <button
                  v-if="!addingTool"
                  class="ac-module-add-btn w-25"
                  :style="{ borderColor: moduleTypeColor['tool'] }"
                  @click="startAdd('tool')"
                >
                  <svg viewBox="0 0 24 24">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </button>

                <!-- Inline Input (real module chip style) -->
                <div
                  v-else
                  class="ac-module-chip ac-module-chip--tools ac-module-chip--input"
                >
                  <input
                    v-model="newToolName"
                    class="ac-chip-input"
                    :autofocus="addingTool"
                    placeholder="New module..."
                    @keyup.enter="saveModule('tool')"
                    @keyup.esc="cancelAdd('tool')"
                    @blur="cancelAdd('tool')"
                    ref="toolInput"
                  />
                </div>
              </div>
            </b-collapse>
          </div>

          <!-- Features -->
          <div class="ac-module-section">
            <div class="ac-section-header ac-section-header--features" @click="featuresCollapsed = !featuresCollapsed">
              <div class="ac-section-title-row">
                <span class="ac-section-dot ac-dot--features"></span>
                <span class="ac-section-title">MMP Features</span>
                <span class="ac-badge ac-badge--features">{{ allModules.filter(m => m.module_type === 'feature').length }}</span>
              </div>
              <div class="ac-chevron" :class="{ open: featuresCollapsed }">
                <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
            <b-collapse v-model="featuresCollapsed">
              <div class="ac-module-grid">
                <div
                  v-for="module in allModules.filter(m => m.module_type === 'feature')"
                  :key="module.module_name"
                  class="ac-module-chip ac-module-chip--features"
                  :class="!module.isactive ? 'inactive-module': ''"
                  v-b-tooltip.hover.right="{ title: module.module_description, customClass: 'ac-tooltip' }"
                  @click="editModule(module)"
                >
                  <span class="ac-chip-label">{{ module.module_name }}</span>
                  <div class="inactive-tag" :style="{visibility: module.isactive ? 'hidden' : 'visible'}">INACTIVE</div>
                </div>

                <!-- Add Button -->
                <button
                  v-if="!addingFeature"
                  class="ac-module-add-btn w-25"
                  :style="{ borderColor: moduleTypeColor['feature'] }"
                  @click="startAdd('feature')"
                >
                  <svg viewBox="0 0 24 24">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </button>

                <!-- Inline Input (real module chip style) -->
                <div
                  v-else
                  class="ac-module-chip ac-module-chip--features ac-module-chip--input"
                >
                  <input
                    v-model="newFeatureName"
                    class="ac-chip-input"
                    placeholder="New module..."
                    @keyup.enter="saveModule('feature')"
                    @keyup.esc="cancelAdd('feature')"
                    @blur="cancelAdd('feature')"
                    ref="featureInput"
                  />
                </div>
              </div>
            </b-collapse>
          </div>

          <!-- Admin -->
          <div class="ac-module-section">
            <div class="ac-section-header ac-section-header--admin" @click="adminCollapsed = !adminCollapsed">
              <div class="ac-section-title-row">
                <span class="ac-section-dot ac-dot--admin"></span>
                <span class="ac-section-title">MMP Administrative Capabilities</span>
                <span class="ac-badge ac-badge--admin">{{ allModules.filter(m => m.module_type === 'admin').length }}</span>
              </div>
              <div class="ac-chevron" :class="{ open: adminCollapsed }">
                <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
            <b-collapse v-model="adminCollapsed">
              <div class="ac-module-grid">
                <div
                  v-for="module in allModules.filter(m => m.module_type === 'admin')"
                  :key="module.module_name"
                  class="ac-module-chip ac-module-chip--admin"
                  :class="!module.isactive ? 'inactive-module': ''"
                  v-b-tooltip.hover.right="{ title: module.module_description, customClass: 'ac-tooltip' }"
                  @click="editModule(module)"
                >
                  <span class="ac-chip-label">{{ module.module_name }}</span>
                  <div class="inactive-tag" :style="{visibility: module.isactive ? 'hidden' : 'visible'}">INACTIVE</div>
                </div>

                <!-- Add Button -->
                <button
                  v-if="!addingAdmin"
                  class="ac-module-add-btn w-25"
                  :style="{ borderColor: moduleTypeColor['admin'] }"
                  @click="startAdd('admin')"
                >
                  <svg viewBox="0 0 24 24">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </button>

                <!-- Inline Input (real module chip style) -->
                <div
                  v-else
                  class="ac-module-chip ac-module-chip--admin ac-module-chip--input"
                >
                  <input
                    v-model="newAdminName"
                    class="ac-chip-input"
                    placeholder="New module..."
                    @keyup.enter="saveModule('admin')"
                    @keyup.esc="cancelAdd('admin')"
                    @blur="cancelAdd('admin')"
                    ref="adminInput"
                  />
                </div>
              </div>
            </b-collapse>
          </div>
        </b-tab>
      </b-tabs>
    </b-modal>

    <!-- Sub-modals -->
    <MMP_UpdateAccessRole
      :row="selectedUpdateRow"
      :isUpdate="isUpdate"
      :user="user"
      :allModules="allModules"
      :organizations="organizations"
      @refresh="getRoles"
      @refreshUserModules="$emit('refreshUserModules')"
    ></MMP_UpdateAccessRole>

    <b-modal
      id="statusConfirmationModal"
      size="md"
      content-class="menu-bg-blue-transparent-modal sc-modal"
      :title="this.selectedUpdateRow.isactive ? 'Deactivate Role' : 'Activate Role'"
      hide-footer
     
    >
      <div v-if="selectedUpdateRow" class="ac-confirm-body">
        <p class="ac-confirm-text">
          Role '<b class="ac-highlight">{{ selectedUpdateRow.role_name }}</b>' will be set to
          <b class="ac-highlight">{{ selectedUpdateRow.isactive ? 'INACTIVE' : 'ACTIVE' }}</b>.
        </p>
        <div class="ac-confirm-actions">
          <b-button variant="primary" @click="changeStatus">Confirm</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="orgStatusChangeModal"
      size="md"
      content-class="menu-bg-blue-transparent-modal oc-modal"
      :title="this.selectedOrg.isactive ? 'Deactivate Organization' : 'Activate Organization'"
      hide-footer
     
    >
      <div v-if="selectedOrg" class="ac-confirm-body">
        <p class="ac-confirm-text">
          Organization '<b class="ac-highlight">{{ selectedOrg.company_name }}</b>' will be set to
          <b class="ac-highlight">{{ selectedOrg.isactive ? 'INACTIVE' : 'ACTIVE' }}</b>.
        </p>
        <div class="ac-confirm-actions">
          <b-button variant="primary" @click="changeOrgStatus">Confirm</b-button>
        </div>
      </div>
    </b-modal>

    <MMP_OrganizationsModal :company="selectedCompany" @refresh="initTables"></MMP_OrganizationsModal>
    <MMP_Modules 
      :updateModule="updateModule" 
      :newModule="newModule"
      @removeNewModule="removeNewModule($event)"
      @resetModules="getAllModules()"
      @refreshUserModules="$emit('refreshUserModules')"
    ></MMP_Modules>
  </div>
</template>

<style>
.ac-tabs {
  --ac-text:       #063c6d;
  --ac-text-muted: rgba(6, 60, 109, 0.55);
  --ac-tools:      #0a5499;
  --ac-features:   #1a7d59;
  --ac-admin:      #6c3cba;
  --ac-layers:     #8a6000;
  --ac-radius:     6px;
  --ac-radius-sm:  4px;
}
</style>

<style scoped>
.inactive-tag {
  max-width: 25%;
  font-size: 9px;
  font-weight: 200;
  color: red;
  background-color: pink;
  border-width: 1px;
  border-color: red;
  border-radius: 9px;
  padding: 3px;
  text-shadow: 0.1px 0.1px black !important;
}
.inactive-module {
  opacity: 0.7;
}
/* ─── Design tokens ──────────────────────────────────────────── */
/* NOTE: modal bg is rgba(6,47,86,0.65) — a medium-dark navy.
   All surface/border values need enough opacity to read against it. */

/* ─── Status badges ──────────────────────────────────────────── */
.status-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 20px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.status-active {
  background: rgba(61, 201, 155, 0.12);
  color: #1a7d59;
  border: 1px solid rgba(61, 201, 155, 0.35);
}
.status-inactive {
  background: rgba(240, 100, 100, 0.12);
  color: #c0392b;
  border: 1px solid rgba(240, 100, 100, 0.32);
}

/* ─── Table cell helpers ─────────────────────────────────────── */
.date-cell {
  font-size: 12px;
  color: var(--ac-text-muted);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
}
.module-name-cell {
  display: inline-block;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  font-size: 13px;
}
.module-column-width {
  width: 25% !important;
  max-width: 25% !important;
  text-align: left;
  text-overflow: ellipsis !important;
  overflow: hidden !important;
  white-space: nowrap !important;
}
.name-col-width { width: 5% !important; }

/* ─── Icon buttons ───────────────────────────────────────────── */
.ac-icon-btn {
  padding: 5px 8px;
  border-radius: var(--ac-radius-sm);
  transition: background 0.15s;
  background: transparent;
  border: none;
}
.ac-icon-btn--edit       { color: #0a5499; }
.ac-icon-btn--edit:hover { background: rgba(6, 60, 109, 0.08); }
.ac-icon-btn--activate       { color: #1a7d59; }
.ac-icon-btn--activate:hover { background: rgba(26, 125, 89, 0.1); }
.ac-icon-btn--deactivate       { color: #c0392b; }
.ac-icon-btn--deactivate:hover { background: rgba(192, 57, 43, 0.1); }
.ac-icon-btn:disabled { opacity: 0.3; cursor: not-allowed; }

/* ─── Confirmation modals ────────────────────────────────────── */
.ac-confirm-body { padding: 4px 0 8px; }
.ac-confirm-text {
  font-size: 13.5px;
  color: var(--ac-text);
  line-height: 1.65;
  margin-bottom: 20px;
}
.ac-confirm-actions { display: flex; justify-content: center; }
.ac-highlight { color: #063c6d; font-weight: 700; }

/* ─── Section headers ────────────────────────────────────────── */
.ac-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: var(--ac-radius);
  cursor: pointer;
  user-select: none;
  margin-top: 6px;
  margin-bottom: 2px;
  transition: background 0.15s;
  border-left: 3px solid transparent;
  background: rgba(6, 60, 109, 0.04);
}
.ac-section-header:hover { background: rgba(6, 60, 109, 0.08); }

.ac-section-header--nested {
  padding: 7px 12px;
  margin-left: 16px;
  background: rgba(6, 60, 109, 0.03);
  border-left-color: rgba(6, 60, 109, 0.2);
}
.ac-section-header--nested:hover { background: rgba(6, 60, 109, 0.06); }

/* Left-border accent per category */
.ac-section-header--tools    { border-left-color: var(--ac-tools); }
.ac-section-header--features { border-left-color: var(--ac-features); }
.ac-section-header--admin    { border-left-color: var(--ac-admin); }
.ac-section-header:not(.ac-section-header--tools):not(.ac-section-header--features):not(.ac-section-header--admin):not(.ac-section-header--nested) {
  border-left-color: var(--ac-layers);
}

.ac-section-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ac-section-title {
  font-size: 13.5px;
  font-weight: 500;
  color: var(--ac-text);
  letter-spacing: 0.01em;
}
.ac-section-title--sm { font-size: 12.5px; }

/* Colored dots */
.ac-section-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.ac-dot--tools    { background: var(--ac-tools); }
.ac-dot--features { background: var(--ac-features); }
.ac-dot--admin    { background: var(--ac-admin); }
.ac-dot--layers   { background: var(--ac-layers); }
.ac-dot--part     { background: var(--ac-text-muted); }

/* Count badges */
.ac-badge {
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 20px;
}
.ac-badge--tools    { background: rgba(10, 84, 153, 0.1); color: var(--ac-tools); }
.ac-badge--features { background: rgba(26, 125, 89, 0.1); color: var(--ac-features); }
.ac-badge--admin    { background: rgba(108, 60, 186, 0.1); color: var(--ac-admin); }

/* Chevron */
.ac-chevron {
  width: 16px; height: 16px;
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.2s ease;
  color: var(--ac-text-muted);
  opacity: 0.7;
}
.ac-chevron.open { transform: rotate(180deg); }
.ac-chevron svg {
  width: 13px; height: 13px;
  stroke: currentColor; fill: none;
  stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
}

/* ─── Collapse body ──────────────────────────────────────────── */
.ac-collapse-body { padding-left: 12px; }

/* ─── Layer grid & checkboxes ────────────────────────────────── */
.ac-layer-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 12px 4px 16px 18px;
}
.ac-layer-item { display: flex; align-items: center; }

.ac-checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 12.5px;
  color: var(--ac-text);
  margin: 0;
}

.ac-checkbox { display: none; }
.ac-checkbox-custom {
  width: 14px; height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(6, 60, 109, 0.3);
  background: rgba(6, 60, 109, 0.04);
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
  position: relative;
}
.ac-checkbox:checked + .ac-checkbox-custom {
  background: var(--ac-tools);
  border-color: var(--ac-tools);
}
.ac-checkbox:checked + .ac-checkbox-custom::after {
  content: '';
  position: absolute;
  left: 3px; top: 1px;
  width: 5px; height: 8px;
  border: 1.5px solid #fff;
  border-top: none; border-left: none;
  transform: rotate(45deg);
}

.ac-layer-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}
.ac-layer-status { display: flex; align-items: center; margin-left: 4px; }
.ac-layer-status--saved { color: var(--ac-features); }

/* ─── Module section ─────────────────────────────────────────── */
.ac-module-section { margin-bottom: 6px; }

.ac-module-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  padding: 10px 4px 14px;
}
.ac-module-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 11px;
  background: rgba(6, 60, 109, 0.04);
  border: 1px solid rgba(6, 60, 109, 0.12);
  border-radius: var(--ac-radius-sm);
  cursor: default;
  transition: background 0.15s, border-color 0.15s;
}
.ac-module-chip:hover                { background: rgba(6, 60, 109, 0.08); cursor: pointer; }
.ac-module-chip--tools:hover         { border-color: rgba(10, 84, 153, 0.4); }
.ac-module-chip--features:hover      { border-color: rgba(26, 125, 89, 0.4); }
.ac-module-chip--admin:hover         { border-color: rgba(108, 60, 186, 0.4); }

.ac-chip-label {
  font-size: 12px;
  font-weight: 400;
  color: var(--ac-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ─── Add Button ─────────────────────────────────────────── */

.ac-module-add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--ac-tools);
  border-radius: var(--ac-radius-sm);
  cursor: pointer;
  padding: 6px;
  transition: background 0.15s ease, transform 0.15s ease;
  color: var(--ac-text-muted);
  /* margin-left: 3px; */
}

.ac-module-add-btn:hover {
  background: rgba(6, 60, 109, 0.08);
  color: var(--ac-text);
  transform: scale(1.05);
}

.ac-module-add-btn svg {
  width: 16px;
  height: 16px;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
}

/* ─── Input State (real chip appearance) ───────────────────── */

.ac-module-chip--input {
  padding: 6px 11px;
  background: rgba(6, 60, 109, 0.04);
  border: 1px solid rgba(6, 60, 109, 0.15);
}

.ac-chip-input {
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  font-size: 12px;
  color: var(--ac-text);
}

.ac-chip-input::placeholder {
  color: var(--ac-text-muted);
}
</style>

<!-- Global style for tooltip (can't be scoped) -->
<style>
.ac-tooltip .tooltip-inner {
  background: #1e2d3d;
  border: 1px solid #2a3441;
  color: #e6edf3;
  font-size: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  max-width: 220px;
  text-align: left;
}
.ac-tooltip .arrow::before { border-right-color: #2a3441; border-left-color: #2a3441; }

/* ── AC modal table + tabs ──────────────────────────────────── */
.ac-modal .menu-bg-blue-transparent,
.ac-modal .menu-bg-blue-transparent-modal {
  background: transparent !important;
  color: #063c6d !important;
  text-shadow: none !important;
}
.ac-modal table { color: #063c6d !important; text-shadow: none !important; }
.ac-modal thead th {
  background: rgba(6, 60, 109, 0.06) !important;
  color: #063c6d !important;
  border-color: rgba(6, 60, 109, 0.12) !important;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-shadow: none !important;
}
.ac-modal thead th a,
.ac-modal thead th svg { color: rgba(6, 60, 109, 0.5) !important; }
.ac-modal tbody tr { color: #063c6d !important; text-shadow: none !important; }
.ac-modal tbody tr:hover { background: rgba(6, 60, 109, 0.04) !important; }
.ac-modal tbody td {
  color: #063c6d !important;
  border-color: rgba(6, 60, 109, 0.08) !important;
  text-shadow: none !important;
}
.ac-modal .footer-pagination-row td,
.ac-modal .footer-pagination-row th {
  background: rgba(6, 60, 109, 0.06) !important;
  color: #063c6d !important;
  border-color: rgba(6, 60, 109, 0.12) !important;
  text-shadow: none !important;
}
.ac-modal .nav-pills .nav-link {
  color: rgba(6, 60, 109, 0.65);
  background: transparent;
  border: 1px solid rgba(6, 60, 109, 0.3);
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  margin: 0 4px;
  transition: background 0.15s;
}
.ac-modal .nav-pills .nav-link.active {
  background: #063c6d !important;
  color: #fff !important;
  border-color: #063c6d !important;
}
/* ── Confirm sub-modal text ─────────────────────────────────── */
.sc-modal .ac-confirm-text,
.oc-modal .ac-confirm-text { color: #063c6d !important; }
.sc-modal .ac-highlight,
.oc-modal .ac-highlight { color: #063c6d !important; font-weight: 700; }
</style>

<script>
import MMP_UpdateAccessRole from './MMP_UpdateAccessRole.vue';
import VueBootstrap4Table from 'vue-bootstrap4-table'
import MMP_OrganizationsModal from './MMP_OrganizationsModal.vue';
import MMP_Modules from './MMP_Modules.vue';
import axios from 'axios'

export default {
  name: 'MMP_AccessControl',
  components: {
    VueBootstrap4Table,
    MMP_UpdateAccessRole,
    MMP_OrganizationsModal,
    MMP_Modules
  },
  props: ['user'],
  data() {
    return {
      accessRoles: [],
      classes: {
        table: {
          'menu-bg-blue-transparent': true
        }
      },
      orgColumns: [
        { label: 'Company ID',   name: 'id',           sort: true  },
        { label: 'Name',         name: 'company_name', sort: true  },
        { label: 'Description',  name: 'description',  sort: false },
        { label: 'Last Updated', name: 'updated_at',   sort: true  },
        { label: 'Update',       name: 'update',       sort: false },
        { label: 'Status',       name: 'isactive',     sort: false }
      ],
      accessRolesColumns: [
        { label: 'Name',                name: 'role_name',     sort: true,  column_classes: 'name-col-width' },
        { label: 'Last Updated Date',   name: 'modified_date', sort: true  },
        { label: 'Last Updated By',     name: 'modified_user', sort: true  },
        { label: 'Modules',             name: 'module_names',  sort: false, column_test_alignment: 'text-left' },
        { label: 'Status',              name: 'isactive',      sort: true  },
        { label: 'Activate / Deactivate', name: 'activate',   sort: false },
        { label: 'Update',              name: 'update',        sort: false }
      ],
      config: {
        checkbox_rows: false,
        rows_selectable: true,
        card_mode: false,
        global_search: {
          placeholder: 'Search...',
          visibility: true,
          showClearButton: true,
          searchOnPressEnter: false
        },
        highlight_row_hover_color: 'rgba(6,60,109,0.04)',
        show_refresh_button: false,
        show_reset_button: false
      },
      actions: [
        { btn_text: "Add New Role", event_name: "add", class: "btn btn-primary" }
      ],
      orgActions: [
        { btn_text: "Add New Organization", event_name: "add", class: "btn btn-primary" }
      ],
      selectedUpdateRow: {},
      selectedOrg: {},
      isUpdate: true,
      organizations: [],
      selectedCompany: null,
      hoveredOrgId: null,
      layers: [],
      changesMade: false,
      allModules: [],
      toolsCollapsed: true,
      featuresCollapsed: true,
      adminCollapsed: true,
      addingTool: false,
      addingFeature: false,
      addingAdmin: false,
      newToolName: '',
      newFeatureName: '',
      newAdminName: '',
      updateModule: null,
      newModule: null
    }
  },
  methods: {
    collapseProject(layerGroup) {
      layerGroup.collapsed = !layerGroup.collapsed
    },
    collapsePartNumber(partNumber) {
      partNumber.collapsed = !partNumber.collapsed
    },
    layerChange(layer, partNumber) {
      layer.saved = false
      layer.spinner = true
      if (sessionStorage.token) {
        axios.put(process.env.API_LOCATION + '/api/layer/updateLayer', {
          id: layer.layer_id,
          release_id: partNumber.release_id,
          visibility: layer.visibility
        }, {
          headers: { 'x-auth-token': sessionStorage.token }
        }).then(response => {
          const content = response.data
          if (content.errors) {
            console.log(content.errors)
          } else {
            layer.saved = true
            setTimeout(() => { layer.spinner = false }, 3000)
          }
        }).catch(error => console.log(error))
      } else {
        console.log('No User Token')
      }
    },
    getLayers() {
      if (sessionStorage.token) {
        axios.get(process.env.API_LOCATION + '/api/layer/getCustomerLayers', {
          headers: { 'x-auth-token': sessionStorage.token }
        }).then(response => {
          const content = response.data
          if (content.errors) {
            console.log(content.errors)
          } else {
            let result = []
            content.forEach((layer) => {
              let projectGroup = result.find(p => p.project == layer.project)
              if (!projectGroup) {
                projectGroup = { project: layer.project, project_id: layer.project_id, part_numbers: [], collapsed: false }
                result.push(projectGroup)
              }
              let partNumberGroup = projectGroup.part_numbers.find(pn => pn.part_number == layer.part_number)
              if (!partNumberGroup) {
                partNumberGroup = { part_number: layer.part_number, release_id: layer.release_id, layers: [], collapsed: false }
                projectGroup.part_numbers.push(partNumberGroup)
              }
              partNumberGroup.layers.push({
                layer_id: layer.layer_id,
                layer_name: layer.layer_name,
                link_object: layer.link_object,
                visibility: layer.layer_visibility == undefined ? true : layer.layer_visibility,
                display_name: layer.link_object.name.replace(/_/g, ' '),
                spinner: false,
                saved: true
              })
            })
            this.layers = result
            this.changesMade = false
          }
        }).catch(error => console.log(error))
      } else {
        console.log('No User Token')
      }
    },
    changeOrgStatus() {
      if (sessionStorage.token) {
        axios.put(process.env.API_LOCATION + '/api/company/status',
          { id: this.selectedOrg.id, status: !this.selectedOrg.isactive },
          { headers: { 'x-auth-token': sessionStorage.token } }
        ).then(response => {
          const content = response.data
          if (content.errors) {
            console.log(content.errors)
          } else {
            this.$bvToast.toast(content.message, {
              autoHideDelay: 8000, title: 'Submission Successful',
              variant: 'success', toaster: 'b-toaster-top-right'
            })
            this.initTables()
            this.$bvModal.hide('orgStatusChangeModal')
          }
        }).catch(error => console.log(error))
      }
    },
    changeOrgStatusHandler(row) {
      this.selectedOrg = row
      this.$bvModal.show('orgStatusChangeModal')
    },
    initTables() {
      this.getRoles()
      this.getCompanies()
      this.getLayers()
    },
    initUpdateRole(row) {
      this.selectedUpdateRow = row
      this.isUpdate = true
      this.$bvModal.show('AccessRoleModal')
    },
    initUpdateOrg(row) {
      this.selectedCompany = row
      this.$bvModal.show('OrganizationsModal')
    },
    initAddRole() {
      this.selectedUpdateRow = {}
      this.isUpdate = false
      this.$bvModal.show('AccessRoleModal')
    },
    initAddOrg() {
      this.selectedCompany = null
      this.$bvModal.show('OrganizationsModal')
    },
    displaySortedModuleNames(modules) {
      return [...modules].sort().join(', ')
    },
    getRoles() {
      if (sessionStorage.token) {
        axios.get(process.env.API_LOCATION + '/api/users/getAllRoles', {
          headers: { 'x-auth-token': sessionStorage.token }
        }).then(response => {
          const content = response.data
          if (content.errors) { console.log(content.errors) }
          else { this.accessRoles = content.rows }
        }).catch(error => console.log(error))
      } else { console.log('No User Token') }
    },
    initStatusChangeModal(row) {
      this.selectedUpdateRow = row
      this.$bvModal.show('statusConfirmationModal')
    },
    changeStatus() {
      if (sessionStorage.token) {
        axios.get(process.env.API_LOCATION + '/api/users/setStatus', {
          headers: { 'x-auth-token': sessionStorage.token },
          params: { id: this.selectedUpdateRow.user_roles_id, status: !this.selectedUpdateRow.isactive }
        }).then(response => {
          const content = response
          if (content.errors) { console.log(content.errors) }
          else {
            this.$bvToast.toast(content.data.msg, {
              autoHideDelay: 7000, variant: 'success',
              title: 'Role Status', toaster: 'b-toaster-top-right'
            })
            this.$bvModal.hide('statusConfirmationModal')
            this.getRoles()
          }
        }).catch(error => console.log(error))
      } else { console.log('No User Token') }
    },
    getCompanies() {
      if (sessionStorage.token) {
        axios.get(process.env.API_LOCATION + '/api/company/', {
          headers: { 'x-auth-token': sessionStorage.token }
        }).then(response => {
          const content = response.data
          if (content.errors) { console.log('Companies API Call Error: ' + content.errors) }
          else { this.organizations = content }
        }).catch(error => console.log(error))
      } else { console.log('No User Token') }
    },
    getAllModules() {
      if (sessionStorage.token) {
        axios.get(process.env.API_LOCATION + '/api/users/getAllModules', {
          headers: { 'x-auth-token': sessionStorage.token }
        }).then(response => {
          const content = response.data
          if (content.errors) { console.log(content.errors) }
          else {
            this.allModules = content.rows.sort((a, b) => {
              const modA = a.module_name.toLowerCase()
              const modB = b.module_name.toLowerCase()
              if (modA < modB) return -1
              if (modA > modB) return 1
              return 0
            })
          }
        }).catch(error => console.log(error))
      } else { console.log('No User Token') }
    },
    startAdd(type) {
      if (type === 'tool') {
        this.addingTool = true
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.$refs.toolInput?.focus()
          })
        })
      }
      if (type === 'feature') {
        this.addingFeature = true
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.$refs.featureInput?.focus()
          })
        })
      }
      if (type === 'admin') {
        this.addingAdmin = true
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.$refs.adminInput?.focus()
          })
        })
      }
    },
    cancelAdd(type) {
      if (type === 'tool') {
        this.addingTool = false
        this.newToolName = ''
      }
      if (type === 'feature') {
        this.addingFeature = false
        this.newFeatureName = ''
      }
      if (type === 'admin') {
        this.addingAdmin = false
        this.newAdminName = ''
      }
    },

    saveModule(type) {
      let name = ''
      if (type === 'tool') name = this.newToolName
      if (type === 'feature') name = this.newFeatureName
      if (type === 'admin') name = this.newAdminName

      // Trim whitespace
      name = name.trim()
      if (!name) return

      const duplicate = this.allModules.some(
        m => m.module_name.toLowerCase() === name.toLowerCase() && m.module_type === type
      )
      if (duplicate) {
        // Optionally show an alert or notification
        this.$bvToast.toast(`A ${type} with the name "${name}" already exists.`, {
          title: 'New Module Error',
          variant: 'danger',
          solid: true
        })
        return
      }

      let mod = {
        module_name: name,
        module_type: type,
        module_description: '',
        isactive: true
      }

      this.updateModule = null
      this.newModule = mod

      this.allModules.push(mod)

      this.$bvModal.show('modulesModal')

      this.cancelAdd(type)
    },
    editModule(module) {
      this.updateModule = null
      this.$nextTick(() => {
        this.updateModule = module
        this.newModule = null
        this.$bvModal.show('modulesModal')
      })
    },
    removeNewModule(module) {
      // Find index of the module in allModules
      const index = this.allModules.findIndex(
        m => m.module_name === module.module_name && m.module_type === module.module_type
      )

      // If found, remove it
      if (index !== -1) {
        this.allModules.splice(index, 1)
      }
    }
  },
  computed: {
    moduleTypeColor() {
      return {
        tool: 'var(--ac-tools)',
        feature: 'var(--ac-features)',
        admin: 'var(--ac-admin)'
      }
    }
  },
  mounted() {
    this.initTables()
    this.getLayers()
    this.getAllModules()
  }
}
</script>